import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { useMutation } from '@apollo/client/react'
import cx from 'helpers/cx'

import { CheckIcon } from '@pleio/icons/check'

import { Avatar } from 'js/components/Avatar'
import { DisplayDate } from 'js/components/DisplayDate'
import IconButton from 'js/components/IconButton'
import { NOTIFICATIONS_UNREAD_QUERY } from 'js/components/NavigationNotifications/NavigationNotifications'
import {
    GroupAvatar,
    type GroupAvatarFieldsFragment,
} from 'js/group/GroupAvatar'
import sanitizeArticle from 'js/lib/helpers/sanitize/article'

import { NOTIFICATIONS_QUERY } from '../Notifications'

import styles from './Notification.module.css'
import { MARK_AS_READ } from './Notification.queries'
import Highlight from './NotificationHighlight'

type NavLinkWrapperProps = {
    url: string | null
    navigateTo: () => void
    children: React.ReactNode
}

const NavLinkWrapper = ({ url, navigateTo, children }: NavLinkWrapperProps) => {
    if (!url) {
        return <div className="NotificationLink VideoLink">{children}</div>
    }

    return (
        <Link to={url} className="NotificationLink" onClick={navigateTo}>
            {children}
        </Link>
    )
}

type NotificationEntity = {
    __typename: string
    guid?: string
    title?: string
    name?: string
    url?: string
    chat?: { guid?: string }
}

type NotificationPerformer = {
    username?: string
    icon?: string
    name?: string
}

type NotificationType = {
    id: string
    entity?: NotificationEntity
    action: string
    performer?: NotificationPerformer
    isUnread: boolean
    customMessage?: string
    container?: (GroupAvatarFieldsFragment & { url: string | null }) | null
    timeCreated: string
    description?: string
}

type Props = {
    notification: NotificationType
    hideNotifications?: () => void
}

const Notification = ({ notification, hideNotifications }: Props) => {
    const { t } = useTranslation()

    const [mutate, { loading }] = useMutation<any>(MARK_AS_READ)

    const {
        id,
        entity,
        action,
        performer,
        isUnread,
        customMessage,
        container,
        timeCreated,
        description,
    } = notification

    if (!entity && action !== 'custom') {
        return null
    }
    const isTagCollection = action === 'createdTagCollection'

    const markAsRead = (evt: React.MouseEvent) => {
        evt.stopPropagation()

        mutate({
            variables: {
                input: {
                    id,
                },
            },
            refetchQueries: [NOTIFICATIONS_UNREAD_QUERY, NOTIFICATIONS_QUERY],
        })
    }

    const navigateTo = () => {
        // Mark as read when navigated from Unread notifications
        if (isUnread) {
            mutate({
                variables: {
                    input: {
                        id,
                    },
                },
                refetchQueries: [
                    NOTIFICATIONS_UNREAD_QUERY,
                    NOTIFICATIONS_QUERY,
                ],
            })
        }

        hideNotifications?.()
    }

    const getActivityLabel = (typeName) => {
        let label = ''
        switch (typeName) {
            case 'Blog':
                label = t('notifications.created-blog')
                break
            case 'Discussion':
                label = t('notifications.created-discussion')
                break
            case 'Event':
                label = t('notifications.created-event')
                break
            case 'StatusUpdate':
                label = t('notifications.created-update')
                break
            case 'Question':
                label = t('notifications.created-question')
                break
            case 'Wiki':
                label = t('notifications.created-wiki')
                break
            case 'File':
                label = t('notifications.uploaded-file')
                break
            case 'Pad':
                label = t('notifications.created-pad')
                break
            default:
                label = t('notifications.created-default')
        }
        return label
    }

    let activity
    switch (action) {
        case 'commented':
            activity = t('notifications.replied-to')
            break

        case 'mentioned':
            activity =
                entity.__typename === 'Comment'
                    ? t('notifications.mentioned-you-in-comment')
                    : entity.__typename === 'ChatMessage'
                      ? t('notifications.mentioned-you-in-chat-message')
                      : t('notifications.mentioned-you-in')
            break

        case 'created':
            activity = getActivityLabel(entity.__typename)
            break

        case 'createdTagCollection':
            activity = getActivityLabel(entity.__typename)
            break
    }

    let item
    if (action === 'custom') {
        item = customMessage
    } else if (entity.title) {
        item = entity.title
    } else if (entity.name) {
        item = entity.name
    } else if (entity.__typename === 'StatusUpdate') {
        item = t('notifications.an-update')
    }

    const url =
        action === 'welcome'
            ? `/user/${performer.username}/settings#notifications`
            : entity.__typename === 'ChatMessage'
              ? `/chat/${entity?.chat?.guid}`
              : entity?.url || container?.url || null

    const inGroup = container?.name

    const itemHasLink = !!entity?.url

    const avatarSize = isUnread ? 'small' : 'normal'

    return (
        <li className={cx(styles.wrapper, isUnread && styles.isUnread)}>
            <NavLinkWrapper url={url} navigateTo={navigateTo}>
                {(performer || container) && (
                    <div className="NotificationAvatar">
                        {performer ? (
                            <Avatar
                                disabled
                                size={avatarSize}
                                image={performer.icon}
                                name={performer.name}
                            />
                        ) : (
                            <GroupAvatar group={container} size={avatarSize} />
                        )}
                    </div>
                )}
                <div style={{ flexGrow: 1 }} id={entity?.guid}>
                    {action === 'welcome' ? (
                        t('notifications.welcome')
                    ) : action === 'custom' ? (
                        <Highlight>{sanitizeArticle(customMessage)}</Highlight>
                    ) : (
                        <>
                            {performer ? (
                                <Highlight $isBold>{performer?.name}</Highlight>
                            ) : (
                                t('notifications.someone')
                            )}{' '}
                            {activity}{' '}
                            {item && (
                                <Highlight
                                    $isBold
                                    className={
                                        itemHasLink && 'NotificationLinkText'
                                    }
                                >
                                    {item}
                                </Highlight>
                            )}
                            {isTagCollection &&
                                description &&
                                ` (${description})`}
                            {inGroup && (
                                <>
                                    {` ${t('notifications.in')} `}
                                    <Highlight
                                        $isBold
                                        className={
                                            !itemHasLink &&
                                            'NotificationLinkText'
                                        }
                                    >
                                        {container.name}
                                    </Highlight>
                                </>
                            )}
                        </>
                    )}
                </div>
                <div className="NotificationDate">
                    <DisplayDate
                        date={timeCreated}
                        type={isUnread ? 'timeSince' : 'shortDate'}
                    />
                </div>
            </NavLinkWrapper>

            {isUnread && (
                <IconButton
                    size="normal"
                    variant="secondary"
                    showOutline
                    tooltip={t('notifications.read')}
                    onClick={markAsRead}
                    loading={loading}
                    className="MarkAsReadButton"
                    aria-describedby={entity?.guid}
                    tooltipProps={{ side: 'left' }}
                >
                    <CheckIcon />
                </IconButton>
            )}
        </li>
    )
}

export default Notification
